import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Select, Input, Space, Button, Alert, Spin, Segmented, Tag, Row, Col, Tooltip,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DownloadOutlined, LinkOutlined,
  AppstoreOutlined, ApiOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import api from '../libs/api';

const KIND_OPTIONS = [
  { label: 'Panel', value: 'panel', icon: <AppstoreOutlined /> },
  { label: 'Inverter', value: 'inverter', icon: <ApiOutlined /> },
  { label: 'Battery', value: 'battery', icon: <ThunderboltOutlined /> },
];

// Columns the upstream list returns as an ISO datetime
const DATE_FIELDS = new Set(['approvedDate', 'expiryDate']);
// Semicolon-delimited multi-value fields — render as tags so they stay readable
const MULTI_FIELDS = new Set(['applicableStandards', 'inputOutputPorts', 'softwareClient']);

function fmtDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  return isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function CecEquipmentTab() {
  const [kind, setKind] = useState('panel');
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [approxTotal, setApproxTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Query state
  const [searchInput, setSearchInput] = useState('');   // what's typed
  const [search, setSearch] = useState('');             // what's submitted
  const [filterValues, setFilterValues] = useState({}); // { filterId: [values] }
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState({ field: '', direction: '' });

  // Load the filter/column config for the selected kind
  useEffect(() => {
    let cancelled = false;
    setConfigLoading(true);
    setError(null);
    setConfig(null);
    api.get(`/api/cec/${kind}/config`, { timeout: 60_000 })
      .then(res => { if (!cancelled) setConfig(res.data.data); })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to load CEC config');
      })
      .finally(() => { if (!cancelled) setConfigLoading(false); });
    return () => { cancelled = true; };
  }, [kind]);

  // Reset query state when switching kind (filter ids differ per kind)
  useEffect(() => {
    setFilterValues({});
    setSearch('');
    setSearchInput('');
    setPage(1);
    setSort({ field: '', direction: '' });
  }, [kind]);

  const buildParams = useCallback(() => {
    const params = { page, pageSize };
    if (search) params.search = search;
    if (sort.field) { params.sortField = sort.field; params.sortDirection = sort.direction; }
    for (const [id, vals] of Object.entries(filterValues)) {
      if (Array.isArray(vals) && vals.length > 0) params[id] = vals.join(',');
    }
    return params;
  }, [page, pageSize, search, sort, filterValues]);

  // Run the search whenever the query changes (config must be loaded first so
  // the backend can validate filter ids)
  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/api/cec/${kind}/search`, { params: buildParams(), timeout: 90_000 })
      .then(res => {
        if (cancelled) return;
        const d = res.data.data;
        setRows(d.rows || []);
        setPageCount(d.pageCount || 0);
        setApproxTotal(d.approxTotal || 0);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Search failed');
        setRows([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, config, buildParams]);

  const submitSearch = () => { setSearch(searchInput.trim()); setPage(1); };
  const resetAll = () => {
    setSearchInput(''); setSearch(''); setFilterValues({});
    setPage(1); setSort({ field: '', direction: '' });
  };

  const activeFilterCount = useMemo(
    () => Object.values(filterValues).reduce((n, v) => n + (v?.length || 0), 0),
    [filterValues]
  );

  const columns = useMemo(() => (config?.headings || []).map(h => ({
    title: h.text,
    dataIndex: h.id,
    key: h.id,
    width: MULTI_FIELDS.has(h.id) ? 260 : (DATE_FIELDS.has(h.id) ? 130 : 170),
    sorter: h.sorting ? true : undefined,
    sortOrder: sort.field === h.id ? (sort.direction === 'asc' ? 'ascend' : 'descend') : null,
    ellipsis: !MULTI_FIELDS.has(h.id),
    render: (v) => {
      if (v === '' || v == null) return <span style={{ color: '#ccc' }}>-</span>;
      if (DATE_FIELDS.has(h.id)) return fmtDate(v);
      if (MULTI_FIELDS.has(h.id)) {
        const parts = String(v).split(';').map(s => s.trim()).filter(Boolean);
        return (
          <span>
            {parts.map((p, i) => (
              <Tag key={i} style={{ marginBottom: 2, fontSize: 11 }}>{p}</Tag>
            ))}
          </span>
        );
      }
      return String(v);
    },
  })), [config, sort]);

  // Server-side sorting — Table only reports the change, we re-query
  const handleTableChange = (pagination, _filters, sorter) => {
    if (pagination.current !== page) setPage(pagination.current);
    if (pagination.pageSize !== pageSize) { setPageSize(pagination.pageSize); setPage(1); }
    const field = sorter?.order ? sorter.field : '';
    const direction = sorter?.order === 'ascend' ? 'asc' : sorter?.order === 'descend' ? 'desc' : '';
    if (field !== sort.field || direction !== sort.direction) {
      setSort({ field, direction });
      setPage(1);
    }
  };

  // CSV of the current page's rows (upstream has no bulk export)
  const downloadCsv = () => {
    if (rows.length === 0) return;
    setDownloading(true);
    try {
      const cols = (config?.headings || []).map(h => h.id);
      const header = (config?.headings || []).map(h => h.text).join(',');
      const esc = (s) => /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      const body = rows.map(r => cols.map(c => {
        const v = r[c];
        if (v == null) return '';
        return esc(DATE_FIELDS.has(c) ? fmtDate(v) : String(v));
      }).join(',')).join('\n');
      const blob = new Blob([`${header}\n${body}\n`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cec-${kind}-page${page}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  return (
    <>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        Clean Energy Council Approved Products list — live from{' '}
        {config?.sourceUrl ? (
          <a href={config.sourceUrl} target="_blank" rel="noreferrer">
            cleanenergycouncil.org.au <LinkOutlined />
          </a>
        ) : 'cleanenergycouncil.org.au'}
      </div>

      {error && (
        <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }}
          onClose={() => setError(null)} />
      )}

      {/* Kind + free-text search */}
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <Segmented options={KIND_OPTIONS} value={kind} onChange={setKind} />
          <Tooltip title={config?.searchTooltip || ''}>
            <Input
              placeholder={config?.searchTooltip || 'Search…'}
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 380 }}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onPressEnter={submitSearch}
              onClear={() => { setSearchInput(''); setSearch(''); setPage(1); }}
            />
          </Tooltip>
          <Button type="primary" onClick={submitSearch}
            style={{ background: '#54A2CC', borderColor: '#54A2CC' }}>Search</Button>
          <Button icon={<ReloadOutlined />} onClick={resetAll}
            disabled={!search && activeFilterCount === 0 && !sort.field}>Reset</Button>
          <Button icon={<DownloadOutlined />} loading={downloading}
            onClick={downloadCsv} disabled={rows.length === 0}>
            Download page CSV
          </Button>
        </Space>
      </Card>

      {/* Column-derived filters */}
      <Spin spinning={configLoading}>
        {config?.filters?.length > 0 && (
          <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}
            title={<span style={{ fontSize: 13 }}>
              Filters{activeFilterCount > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{activeFilterCount} active</Tag>}
            </span>}>
            <Row gutter={[12, 12]}>
              {config.filters.map(f => (
                <Col key={f.id} xs={24} sm={12} lg={8} xxl={6}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                    {f.text} <span style={{ color: '#bbb' }}>({f.options.length})</span>
                  </div>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={`Any ${f.text}`}
                    style={{ width: '100%' }}
                    size="small"
                    value={filterValues[f.id] || []}
                    onChange={vals => { setFilterValues(prev => ({ ...prev, [f.id]: vals })); setPage(1); }}
                    options={f.options.map(o => ({ label: o, value: o }))}
                    filterOption={(input, opt) => String(opt.label).toLowerCase().includes(input.toLowerCase())}
                    maxTagCount={2}
                    maxTagPlaceholder={omitted => `+${omitted.length}`}
                  />
                </Col>
              ))}
            </Row>
          </Card>
        )}
      </Spin>

      {/* Results */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 13 }}>
            {config?.label || 'Results'}
            <span style={{ color: '#999', fontWeight: 400, marginLeft: 8 }}>
              {loading ? 'searching…' : `~${approxTotal.toLocaleString()} matches · page ${page} of ${pageCount || 1}`}
            </span>
          </span>
        }
      >
        <Table
          dataSource={rows.map((r, i) => ({ ...r, _k: `${kind}-${page}-${i}` }))}
          rowKey="_k"
          columns={columns}
          loading={loading}
          size="small"
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            // Upstream reports pageCount only, so drive the pager off that
            total: pageCount * pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: () => `~${approxTotal.toLocaleString()} approved products`,
          }}
        />
      </Card>
    </>
  );
}
